import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ReadableAccordion';
import { Building2, CheckCircle, Mail, MapPin, Phone, ShieldCheck, Star, Truck, Wrench } from 'lucide-react';
import InlineBookingForm from './InlineBookingForm';
import { HeroProofList } from './HeroProofList';
import { ProofMetric } from './ProofMetric';
import { TechnicianNetworkCta } from './TechnicianNetworkCta';
import { citySeoPages } from '../data/seoCityPages';
import { companyInfo, contactInfo, googleReviewsData } from '../data/mockData';
import { trackEvent } from '../utils/analytics';
import { setPageSeo } from '../utils/seo';
import { scrollToBookingForm } from '../utils/bookingFormHighlight';

const kebab = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const buildCitySchema = (page) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://repair.3dprintergear.com.au/' },
        { '@type': 'ListItem', position: 2, name: page.h1, item: page.canonical }
      ]
    },
    {
      '@type': 'WebPage',
      '@id': `${page.canonical}#webpage`,
      url: page.canonical,
      name: page.title,
      description: page.description,
      isPartOf: { '@id': 'https://repair.3dprintergear.com.au/#website' },
      about: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      primaryImageOfPage: { '@type': 'ImageObject', url: page.image },
      inLanguage: 'en-AU'
    },
    {
      '@type': 'Service',
      '@id': `${page.canonical}#service`,
      name: page.h1,
      serviceType: '3D Printer Repair',
      provider: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      areaServed: {
        '@type': 'City',
        name: page.city,
        containedInPlace: { '@type': 'State', name: page.region }
      },
      availableChannel: {
        '@type': 'ServiceChannel',
        servicePhone: { '@type': 'ContactPoint', telephone: '+611300334327', contactType: 'Repairs and service bookings' },
        serviceUrl: page.canonical
      },
      description: page.heroSubheading,
      offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'AUD', description: 'Free quote before repair work begins.' }
    },
    {
      '@type': 'FAQPage',
      '@id': `${page.canonical}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer }
      }))
    }
  ]
});

export default function SeoCityPage({ cityKey = 'melbourne' }) {
  const page = citySeoPages[cityKey];
  const bookingFormRef = useRef(null);
  const isVictoriaPage = page.region === 'Victoria' || page.city === 'Melbourne';
  const [formInView, setFormInView] = useState(true);
  const schema = useMemo(() => buildCitySchema(page), [buildCitySchema, page]);
  const serviceLinks = useMemo(() => [
    { to: '/', label: '3D Printer Repair Australia', testId: 'city-footer-national-link' },
    { to: '/3d-printer-repair-melbourne', label: '3D Printer Repair Melbourne', testId: 'city-footer-melbourne-link' },
    { to: '/3d-printer-repair-sydney', label: '3D Printer Repair Sydney', testId: 'city-footer-sydney-link' },
    { to: '/3d-printer-repair-brisbane', label: '3D Printer Repair Brisbane', testId: 'city-footer-brisbane-link' },
    { to: '/3d-printer-repair-perth', label: '3D Printer Repair Perth', testId: 'city-footer-perth-link' },
    { to: '/3d-printer-repair-adelaide', label: '3D Printer Repair Adelaide', testId: 'city-footer-adelaide-link' }
  ], [page]);

  useEffect(() => {
    setPageSeo({ title: page.title, description: page.description, canonical: page.canonical, image: page.image });
  }, [page, setPageSeo]);

  useEffect(() => {
    const formNode = bookingFormRef.current;
    if (!formNode) return undefined;
    const observer = new IntersectionObserver(([entry]) => setFormInView(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(formNode);
    return () => observer.disconnect();
  }, [bookingFormRef]);

  const scrollToBooking = (location, label = 'Start repair request') => {
    trackEvent('cta_click', { cta_location: location, cta_label: label });
    scrollToBookingForm(bookingFormRef);
  };

  const handlePhoneClick = (phoneLocation) => trackEvent('phone_click', { phone_location: phoneLocation });

  return (
    <div className="min-h-screen bg-white text-gray-900 pb-20 md:pb-0" data-testid={`seo-city-page-${page.slug}`}>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="city-page-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="city-header-home-link">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 sm:h-12 w-auto" data-testid="city-header-logo-image" />
              <div className="hidden sm:block">
                <p className="text-sm font-extrabold tracking-tight" data-testid="city-header-company-name">{companyInfo.name}</p>
                <p className="text-xs text-gray-500" data-testid="city-header-company-division">Service &amp; Repair Division</p>
              </div>
            </Link>
            <div className="flex items-center gap-3 sm:gap-5">
              <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_header`)} className="hidden md:flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black transition-colors" data-testid="city-header-phone-link">
                <Phone className="h-4 w-4" />
                <span data-testid="city-header-phone-text">{contactInfo.phone} <span className="text-gray-400">·</span> Option 2</span>
              </a>
              <button onClick={() => scrollToBooking(`${page.slug}_header`, 'Book Service')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-3 sm:px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors whitespace-nowrap" data-testid="city-header-book-button">
                <span className="sm:hidden">Book</span><span className="hidden sm:inline">Book Service</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white" data-testid="city-hero-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 lg:py-14">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 items-start">
              <div className="lg:col-span-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-500 mb-3" data-testid="city-hero-eyebrow">{page.eyebrow}</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-4" data-testid="city-hero-heading">{page.h1}</h1>
                <p className="text-base text-gray-600 leading-relaxed max-w-xl mb-5" data-testid="city-hero-subheading">{page.heroSubheading}</p>
                <HeroProofList items={page.proof} testId="city-hero-proof-list" />
                <div className="border border-gray-200 overflow-hidden rounded-sm" data-testid="city-hero-image-wrap">
                  <img src={page.image} alt="Bambu Lab 3D printer repair technician" className="w-full aspect-[16/9] object-cover object-center" data-testid="city-hero-image" />
                </div>
              </div>
              <div className="lg:col-span-7 lg:sticky lg:top-24" data-testid="city-booking-column">
                <InlineBookingForm ref={bookingFormRef} pageSource={`3D Printer Repair ${page.city}`} />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 text-white" data-testid="city-proof-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-700 border border-gray-700">
              {[
                ['700+', 'Google reviews'],
                [page.city, 'Repair support'],
                ['Insured', 'PI + public liability'],
                ['Centre', 'Fully equipped repair centre'],
                ['12+', 'Years experience']
              ].map(([number, label]) => <ProofMetric key={label} number={number} label={label} testIdPrefix="city-proof-metric" />)}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="city-repair-options-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-repair-options-heading">{page.city} repair options</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="city-repair-options-summary">{page.intro}</p>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.repairOptions.map((option) => (
                  <article key={option.title} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`city-repair-option-${kebab(option.title)}`}>
                    <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-gray-500 mb-2" data-testid={`city-repair-option-label-${kebab(option.title)}`}>{option.label}</p>
                    <h3 className="text-lg font-extrabold mb-2" data-testid={`city-repair-option-title-${kebab(option.title)}`}>{option.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`city-repair-option-copy-${kebab(option.title)}`}>{option.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="city-brands-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-brands-heading">Brands we repair in {page.city}</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="city-brands-summary">We support the brands and models below. Include your exact model and symptoms so we can confirm parts availability and the right repair option.</p>
                <button onClick={() => scrollToBooking(`${page.slug}_brands`, 'Start repair request')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="city-brands-cta-button">Start repair request</button>
              </div>
              <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-gray-200 border border-gray-200" data-testid="city-brands-grid">
                {page.brands.map((brand) => (
                  <div key={brand} className="bg-white p-4 min-h-[74px] flex items-center" data-testid={`city-brand-${kebab(brand)}`}>
                    <p className="text-sm font-extrabold text-gray-900">{brand}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="city-common-requests-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-common-requests-heading">Common {page.city} repair requests</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="city-common-requests-summary">These problems usually need more than a quick setting change when they keep returning or affect production, classroom or business printers.</p>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.commonRequests.map((request) => (
                  <button key={request} onClick={() => scrollToBooking(`${page.slug}_common_request`, request)} className="text-left border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-900 p-5 rounded-sm transition-colors" data-testid={`city-common-request-${kebab(request)}`}>
                    <CheckCircle className="h-5 w-5 text-orange-500 mb-3" />
                    <span className="text-sm font-extrabold text-gray-900">{request}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="city-schools-business-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-schools-heading">For {page.city} schools, businesses and serious makers</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="city-schools-summary">Printer downtime can interrupt classes, production jobs and customer deadlines. The {page.city} repair workflow is built for clear communication, documented quotes and reliable service paths.</p>
                {isVictoriaPage ? (
                  <div className="border border-gray-200 bg-white px-4 py-3 rounded-sm text-sm text-gray-700" data-testid="city-service-centre-card">
                    <p className="font-extrabold text-gray-900 mb-1" data-testid="city-service-centre-card-title">Service &amp; Repair Centre</p>
                    <p data-testid="city-service-centre-card-address">{contactInfo.address}</p>
                    <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_service_centre`)} className="inline-flex items-center gap-1.5 mt-2 font-semibold text-gray-900 hover:text-black" data-testid="city-service-centre-phone-link"><Phone className="h-3.5 w-3.5 text-orange-500" />{contactInfo.phone} · Option 2</a>
                  </div>
                ) : (
                  <div className="border border-gray-200 bg-white px-4 py-3 rounded-sm text-sm text-gray-700" data-testid="city-service-path-card">
                    <p className="font-extrabold text-gray-900 mb-1" data-testid="city-service-path-card-title">Repair support pathway</p>
                    <p className="leading-relaxed" data-testid="city-service-path-card-copy">Start with printer details, symptoms and photos. We&apos;ll confirm the best path for {page.city}: remote triage, send-in repair guidance or scheduled on-site support where practical.</p>
                    <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_service_path`)} className="inline-flex items-center gap-1.5 mt-2 font-semibold text-gray-900 hover:text-black" data-testid="city-service-path-phone-link"><Phone className="h-3.5 w-3.5 text-orange-500" />{contactInfo.phone} · Option 2</a>
                  </div>
                )}
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                {[
                  [ShieldCheck, 'Fully insured', 'Professional indemnity and public liability cover.'],
                  [Building2, 'Repair-centre backed', 'Specialist diagnostics, tools and parts workflow.'],
                  [Truck, 'On-site where practical', 'Scheduled support for schools, businesses and larger fleets.'],
                  [Wrench, 'Quote-before-work', 'No repair begins until the quote is clear.']
                ].map(([Icon, title, copy]) => (
                  <article key={title} className="border border-gray-200 bg-white p-5 rounded-sm" data-testid={`city-trust-card-${kebab(title)}`}>
                    <Icon className="h-6 w-6 text-gray-900 mb-3" />
                    <h3 className="text-base font-extrabold mb-1" data-testid={`city-trust-card-title-${kebab(title)}`}>{title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`city-trust-card-copy-${kebab(title)}`}>{copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="city-suburbs-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2" data-testid="city-suburbs-heading">{page.city} areas supported</h2>
                <p className="text-base text-gray-600" data-testid="city-suburbs-summary">Start with the form and we will confirm the best service path for your location and printer issue.</p>
              </div>
              <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_suburbs`)} className="self-start bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="city-suburbs-phone-link">Call Option 2</a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200" data-testid="city-suburbs-grid">
              {page.suburbs.map((suburb) => (
                <div key={suburb} className="bg-gray-50 p-4" data-testid={`city-suburb-${kebab(suburb)}`}>
                  <p className="text-sm font-extrabold text-gray-900">{suburb}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="city-reviews-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 mb-4" data-testid="city-google-rating-summary"><span className="text-2xl font-extrabold">{googleReviewsData.rating}/5</span>{Array.from({ length: 5 }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`city-summary-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-reviews-heading">Repair confidence before you book</h2>
                <p className="text-base text-gray-600" data-testid="city-reviews-summary">Trust signals matter when you are handing over an expensive printer. The goal is a clear quote, practical advice and reliable repair outcome.</p>
              </div>
              <div className="lg:col-span-8 grid md:grid-cols-3 gap-4">
                {googleReviewsData.reviews.map((review) => (
                  <article key={review.id} className="border border-gray-200 bg-white p-5 rounded-sm" data-testid={`city-review-card-${review.id}`}>
                    <div className="flex mb-3">{Array.from({ length: review.rating }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`city-${review.id}-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}</div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4" data-testid={`city-review-content-${review.id}`}>“{review.content}”</p>
                    <p className="text-sm font-bold" data-testid={`city-review-author-${review.id}`}>{review.name}</p>
                    <p className="text-xs text-gray-500">Google Review</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="city-faq-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-faq-heading">{page.city} repair questions</h2>
                <p className="text-base text-gray-600" data-testid="city-faq-summary">Answers for {page.city} customers comparing repair-centre, send-in and on-site options.</p>
              </div>
              <div className="lg:col-span-8">
                <Accordion type="single" collapsible className="border-t border-gray-200" data-testid="city-faq-accordion" onValueChange={(value) => value && trackEvent('faq_open', { faq_id: value, faq_category: `${page.slug}_seo_page` })}>
                  {page.faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`${page.slug}-faq-${index + 1}`} className="border-b border-gray-200" data-testid={`city-faq-item-${index + 1}`}>
                      <AccordionTrigger className="text-left py-4 hover:no-underline" data-testid={`city-faq-question-${index + 1}`}><span className="text-sm font-extrabold text-gray-900 pr-4">{faq.question}</span></AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5" data-testid={`city-faq-answer-${index + 1}`}>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <TechnicianNetworkCta location={`${page.slug}-city-page`} />

        <section className="py-14 bg-gray-900 text-white" data-testid="city-final-cta-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="city-final-cta-heading">Need 3D printer repair in {page.city}?</h2>
            <p className="text-base text-gray-400 mb-6" data-testid="city-final-cta-summary">Start a repair request and our team will confirm the best next step for your printer, location and urgency.</p>
            <button onClick={() => scrollToBooking(`${page.slug}_final_cta`, 'Start repair request')} className="bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-6 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="city-final-cta-button">Start repair request</button>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white py-14 border-t border-gray-800" data-testid="city-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto filter brightness-0 invert mb-4" data-testid="city-footer-logo" />
              <p className="text-gray-400 text-sm leading-relaxed" data-testid="city-footer-description">Australia&apos;s trusted 3D printer service and repair team.</p>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="city-footer-contact-heading">Contact</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <p className="flex items-start gap-3" data-testid="city-footer-address"><MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />{contactInfo.address}</p>
                <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_footer`)} className="flex items-center gap-3 hover:text-white" data-testid="city-footer-phone-link"><Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />{contactInfo.phone} ({contactInfo.phoneOption})</a>
                <a href="mailto:admin@3dprintergear.com.au" onClick={() => trackEvent('email_click', { email_location: `${page.slug}_footer` })} className="flex items-center gap-3 hover:text-white" data-testid="city-footer-email-link"><Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />admin@3dprintergear.com.au</a>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="city-footer-service-pages-heading">Service pages</h3>
              <div className="space-y-2 text-sm text-gray-300">
                {serviceLinks.map((link) => (
                  <Link key={link.to} to={link.to} className="block hover:text-white" data-testid={link.testId}>{link.label}</Link>
                ))}
                <Link to="/bambu-lab-repair-australia" className="block hover:text-white" data-testid="city-footer-bambu-lab-link">Bambu Lab Repair Australia</Link>
                <Link to="/3d-printer-blob-of-death-repair" className="block hover:text-white" data-testid="city-footer-blob-of-death-link">Blob of Death Repair</Link>
                <Link to="/3d-printer-nozzle-clog-repair" className="block hover:text-white" data-testid="city-footer-nozzle-clog-link">Nozzle Clog Repair</Link>
                <Link to="/3d-printer-layer-shifting-repair" className="block hover:text-white" data-testid="city-footer-layer-shifting-link">Layer Shifting Repair</Link>
                <Link to="/3d-printer-bed-adhesion-repair" className="block hover:text-white" data-testid="city-footer-bed-adhesion-link">Bed Adhesion Repair</Link>
                <Link to="/3d-printer-under-extrusion-repair" className="block hover:text-white" data-testid="city-footer-under-extrusion-link">Under-Extrusion Repair</Link>
                <Link to="/3d-printer-repairs-for-schools" className="block hover:text-white" data-testid="city-footer-school-repairs-link">3D Printer Repairs for Schools</Link>
                <Link to="/3d-printer-maintenance-for-schools" className="block hover:text-white" data-testid="city-footer-school-maintenance-link">3D Printer Maintenance for Schools</Link>
                <Link to="/apply-as-technician" className="block hover:text-white" data-testid="city-footer-apply-as-technician-link">Apply as a Technician</Link>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="city-footer-hours-heading">Hours</h3>
              <div className="text-sm text-gray-300 space-y-1 mb-5" data-testid="city-footer-business-hours"><p>{contactInfo.businessHours.weekdays}</p><p>{contactInfo.businessHours.saturday}</p><p>{contactInfo.businessHours.sunday}</p></div>
              <button onClick={() => scrollToBooking(`${page.slug}_footer`, 'Book Service')} className="w-full bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="city-footer-book-button">Book Service</button>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-10 pt-6 text-sm text-gray-500" data-testid="city-footer-copyright">{contactInfo.copyright} {companyInfo.name}. All rights reserved. | {contactInfo.abn} | <a href="https://www.3dprintergear.com.au/privacy-policy/" className="hover:text-gray-300 underline underline-offset-2" data-testid="city-footer-privacy-policy-link">Privacy Policy</a></div>
        </div>
      </footer>

      {!formInView && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-3" data-testid="city-mobile-sticky-cta-wrap">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <button onClick={() => scrollToBooking(`${page.slug}_mobile_sticky`, 'Start Repair Form')} className="bg-gray-900 hover:bg-black text-white text-sm font-extrabold py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="city-mobile-sticky-start-form-button">Start Repair Form</button>
            <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_mobile_sticky`)} className="border border-gray-300 text-gray-900 text-sm font-extrabold px-4 py-3 rounded-sm" data-testid="city-mobile-sticky-call-button">Call Option 2</a>
          </div>
        </div>
      )}
    </div>
  );
}